import { FC, useCallback } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import SquadPitch from '@components/manual-team-entry/squad-pitch';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import { useSquadPortraits } from '@components/quiz/ReadinessProvider/utils/useSquadPortraits';
import { formatUplift } from '@components/quiz/shared/format-uplift';
import type { SanityReadinessTransferBlock } from '@lib/sanity/types';
import type { TransferReplacementCard } from 'types/readiness';

type TransferBlockProps = SanityReadinessTransferBlock & {
  onNext: () => void;
};

// Rank-1 (highest points uplift vs the out player) is the locked "AI's #1
// move" card, ordered first by the server.
const LOCKED_CARD_INDEX = 0;

const PadlockIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const LockedReplacementCard: FC<{
  card: TransferReplacementCard;
  ribbonText: string;
  title: string;
  hint: string;
}> = ({ card, ribbonText, title, hint }) => (
  <div className="relative mt-3">
    <span className="absolute -top-2.5 left-3 z-10 rounded-md bg-primary-500 px-2 py-0.5 font-body text-[10px] font-bold tracking-widest text-white">
      {ribbonText}
    </span>
    <div className="flex items-center gap-4 rounded-xl border-2 border-primary-500 bg-primary-50 px-5 py-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-white">
        <PadlockIcon />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm font-bold text-neutral-800">
          {title}
        </p>
        <p className="truncate font-body text-xs text-neutral-500">{hint}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-heading text-base font-bold text-primary-600">
          {formatUplift(card.pointsUplift)}
        </p>
        <p className="font-body text-[10px] text-neutral-500">vs current</p>
      </div>
    </div>
  </div>
);

const VisibleReplacementCard: FC<{
  card: TransferReplacementCard;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ card, isSelected, onSelect }) => {
  if (!card.player) return null;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all ${
        isSelected
          ? 'border-primary-600 bg-primary-50'
          : 'border-neutral-200 bg-white'
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm font-bold text-neutral-800">
          {card.player.displayName}
        </p>
        <p className="truncate font-body text-xs text-neutral-500">
          {card.player.team.shortName} &middot; £{card.player.price.toFixed(1)}m
          &middot; {card.player.position}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-heading text-base font-bold text-primary-500">
          {formatUplift(card.pointsUplift)}
        </p>
        <p className="font-body text-[10px] text-neutral-400">vs current</p>
      </div>
    </button>
  );
};

/**
 * Step 5: transfer intention. Three replacement cards — the rank-1 card
 * is locked (uplift shown, name hidden); ranks 2-3 are pickable. The CTA
 * enables only on a card selection or the roll option, never on the
 * out-player tap alone (spec 6.1).
 */
const TransferBlock: FC<TransferBlockProps> = ({
  pitchInstruction,
  replacementHeading,
  lockedRibbonText,
  lockedCardTitle,
  lockedCardHint,
  rollOptionText,
  ctaText,
  onNext
}) => {
  const { state, actions } = useReadiness();
  const { pitchTeam } = useSquadPortraits(state.team);

  const handlePlayerTap = useCallback(
    async (playerId: string) => {
      const player = state.team.find(
        (member) => member.player?.id === playerId
      )?.player;
      if (!player) return;
      // Changing the out-player resets the choice — the CTA must re-gate
      actions.setTransferChoice(null);
      await actions.selectTransferOut(player);
    },
    [actions, state.team]
  );

  const transferChoice = state.transferChoice;
  const isRolling = transferChoice?.type === 'roll';
  const replacements = state.transferReplacements;

  return (
    <section className="flex flex-col items-center px-4 pb-16 pt-6">
      {pitchInstruction && (
        <p className="mb-3 max-w-sm text-center font-body text-xs font-semibold uppercase tracking-widest text-neutral-500">
          {pitchInstruction}
        </p>
      )}

      <SquadPitch
        team={pitchTeam}
        onPlayerTap={handlePlayerTap}
        captainId={state.selectedCaptain?.id ?? null}
        selectedPlayerId={state.selectedTransferOut?.id ?? null}
        dimmedPlayerId={state.selectedTransferOut?.id ?? null}
      />

      <div className="w-full max-w-sm">
        {state.selectedTransferOut && (
          <div className="mt-6">
            {replacementHeading && (
              <p className="mb-3 font-body text-xs font-bold uppercase tracking-widest text-neutral-500">
                {replacementHeading}
              </p>
            )}

            {state.isLoadingReplacements && (
              <div className="flex justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
              </div>
            )}

            {state.replacementError && (
              <p className="text-center font-body text-sm text-danger-600">
                {state.replacementError}
              </p>
            )}

            {replacements && replacements.length > 0 && (
              <div className="space-y-3">
                {replacements[LOCKED_CARD_INDEX] && (
                  <LockedReplacementCard
                    card={replacements[LOCKED_CARD_INDEX]}
                    ribbonText={lockedRibbonText ?? "AI'S #1 MOVE"}
                    title={lockedCardTitle ?? "AI's #1 move for this slot"}
                    hint={
                      lockedCardHint ??
                      "Player locked until you're inside AI Transfers"
                    }
                  />
                )}
                {replacements.map((card, index) => {
                  if (index === LOCKED_CARD_INDEX) return null;
                  return (
                    <VisibleReplacementCard
                      key={card.player?.id ?? index}
                      card={card}
                      isSelected={
                        transferChoice?.type === 'replacement' &&
                        transferChoice.index === index
                      }
                      onSelect={() =>
                        actions.setTransferChoice({
                          type: 'replacement',
                          index
                        })
                      }
                    />
                  );
                })}
              </div>
            )}

            {replacements && replacements.length === 0 && (
              <p className="text-center font-body text-sm text-neutral-500">
                No standout replacement for this player right now. You can roll
                the transfer below instead.
              </p>
            )}
          </div>
        )}

        {/* Roll option — always available */}
        {rollOptionText && (
          <button
            type="button"
            onClick={() => actions.setTransferChoice({ type: 'roll' })}
            data-cy="readiness-transfer-roll"
            className={`mt-5 w-full rounded-lg border-2 px-5 py-3.5 text-center font-body text-sm font-semibold transition-all ${
              isRolling
                ? 'border-primary-600 bg-primary-50 text-neutral-800'
                : 'border-dashed border-primary-300 bg-white text-primary-500 hover:border-primary-400'
            }`}
          >
            {rollOptionText}
          </button>
        )}

        <div className="mt-4">
          <CtaButton
            onClick={onNext}
            disabled={!transferChoice}
            dataCy="readiness-transfer-cta"
          >
            {ctaText ?? 'See your readiness verdict'}
          </CtaButton>
        </div>
      </div>
    </section>
  );
};

export default TransferBlock;
